import * as THREE from 'three';
import { containRect, defaultPlacement } from './placement';
import { bindPhotoPlacement } from './dragPlacement';

export function createPhotoScene({ renderer, scene, camera, controls, studio, host, getModel, onChange }) {
  let state = { enabled: false, image: null, placement: defaultPlacement('straight'), compare: false, rect: null };
  const canvas = renderer.domElement;
  const unbind = bindPhotoPlacement(canvas, () => state, onChange);
  const fit = (width = host.clientWidth, height = host.clientHeight) => {
    if (!state.enabled) return;
    const model = getModel(); if (!model || !state.image) return;
    const p = state.placement, rect = containRect(width, height, state.image.naturalWidth, state.image.naturalHeight); state.rect = rect;
    camera.aspect = rect.width / rect.height;
    const bounds = new THREE.Box3().setFromObject(model.group), sphere = bounds.getBoundingSphere(new THREE.Sphere());
    const half = THREE.MathUtils.degToRad(camera.fov / 2), horizontal = Math.atan(Math.tan(half) * camera.aspect);
    const distance = sphere.radius / Math.sin(Math.min(half, horizontal)) * 1.13 / p.scale;
    const yaw = THREE.MathUtils.degToRad(p.yaw), pitch = THREE.MathUtils.degToRad(p.pitch);
    const direction = new THREE.Vector3(Math.sin(yaw) * Math.cos(pitch), Math.sin(pitch), Math.cos(yaw) * Math.cos(pitch));
    camera.position.copy(sphere.center).addScaledVector(direction, distance);
    camera.up.set(0, 1, 0).applyAxisAngle(direction, THREE.MathUtils.degToRad(p.roll)); camera.lookAt(sphere.center);
    camera.setViewOffset(rect.width, rect.height, -p.offset_x * rect.width, -p.offset_y * rect.height, rect.width, rect.height);
    camera.updateProjectionMatrix();
    renderer.setViewport(rect.x, height - rect.y - rect.height, rect.width, rect.height);
    renderer.setScissor(rect.x, height - rect.y - rect.height, rect.width, rect.height); renderer.setScissorTest(true);
  };
  return {
    get active() { return state.enabled; },
    set: next => {
      const leaving = state.enabled && !next.enabled;
      state = { ...state, ...next, placement: next.placement || defaultPlacement('straight') };
      controls.enabled = !state.enabled; studio.setPhotoMode(state.enabled);
      const model = getModel(); if (model) model.group.visible = !state.enabled || (!!state.image && !state.compare);
      canvas.style.opacity = state.enabled ? state.placement.opacity : 1;
      canvas.style.cursor = state.enabled ? state.placement.locked ? 'default' : 'move' : 'grab';
      if (leaving) { camera.clearViewOffset(); camera.up.set(0, 1, 0); renderer.setScissorTest(false); renderer.setViewport(0, 0, host.clientWidth, host.clientHeight); }
      fit(); return leaving;
    },
    fit,
    openingRegions: () => {
      if (!state.enabled || !state.image) return [];
      fit(); camera.updateMatrixWorld(true); const model = getModel(); model.group.updateMatrixWorld(true);
      return model.runs.map(run => [[-run.width / 2, 0], [run.width / 2, 0], [run.width / 2, run.height], [-run.width / 2, run.height]].map(([x, y]) => {
        const point = run.group.localToWorld(new THREE.Vector3(x, y, 0)).project(camera);
        return { x: Math.max(.001, Math.min(.999, (point.x + 1) / 2)), y: Math.max(.001, Math.min(.999, (1 - point.y) / 2)) };
      }));
    },
    beforeRender: () => {
      if (state.enabled) { renderer.setScissorTest(false); renderer.clear(); fit(); }
      Object.assign(canvas.dataset, { photoMode: String(state.enabled), photoLoaded: String(!!state.image), photoScale: String(state.placement.scale), photoOffsetX: String(state.placement.offset_x), photoOffsetY: String(state.placement.offset_y), photoYaw: String(state.placement.yaw), photoPitch: String(state.placement.pitch), photoRoll: String(state.placement.roll), photoLocked: String(state.placement.locked), photoCompare: String(state.compare) });
    },
    capture: async (options = {}) => {
      if (!state.enabled || !state.image) throw new Error('Upload a photo before exporting.');
      const image = state.image, ratio = Math.min(1, 2048 / Math.max(image.naturalWidth, image.naturalHeight));
      const width = Math.round(image.naturalWidth * ratio), height = Math.round(image.naturalHeight * ratio);
      const output = document.createElement('canvas'); output.width = width; output.height = height;
      const ctx = output.getContext('2d'); if (!options.layer) ctx.drawImage(image, 0, 0, width, height);
      const oldRatio = renderer.getPixelRatio();
      const model = getModel(), wasVisible = model.group.visible;
      try {
        if (options.reference) model.group.visible = true;
        renderer.setPixelRatio(1); renderer.setSize(width, height, false); renderer.setScissorTest(false); renderer.clear(); fit(width, height);
        renderer.render(scene, camera); ctx.globalAlpha = options.reference ? 1 : state.placement.opacity; ctx.drawImage(canvas, 0, 0, width, height); ctx.globalAlpha = 1;
      } finally {
        model.group.visible = wasVisible;
        renderer.setPixelRatio(oldRatio); renderer.setSize(host.clientWidth, host.clientHeight); fit(); renderer.render(scene, camera);
      }
      return new Promise((resolve, reject) => output.toBlob(blob => blob ? resolve(blob) : reject(new Error('The image could not be exported.')), 'image/png'));
    },
    dispose: unbind,
  };
}